import React, { createContext, useContext, useEffect, useState } from "react";
import { api, setTokens, clearAuth, getStoredUser, USER_KEY } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(getStoredUser());
  const [loading, setLoading] = useState(true);
  const [forcePasswordChange, setForcePasswordChange] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function bootstrap() {
      // P3-hotfix (2026-07-10) — infinite reload loop fix.
      // Wcześniej bootstrap ZAWSZE wołał /auth/me. Dla niezalogowanego usera to dawało
      // 401 → interceptor próbował /auth/refresh → 401 → window.location.href="/login"
      // → PAGE RELOAD → bootstrap znowu → LOOP.
      // Fix: jeśli localStorage NIE ma user info, to znaczy że użytkownik nie jest
      // zalogowany (LUB zamknął sesję). Pomijamy /auth/me i pokazujemy login page.
      // Edge case: fresh browser z ważnym cookie ale bez localStorage — user zaloguje
      // się jeszcze raz (mały koszt vs katastrofa infinite loop).
      const stored = getStoredUser();
      if (!stored) {
        if (!cancelled) setLoading(false);
        return;
      }
      // Mamy cache — probujemy /auth/me żeby zweryfikować że cookie nadal ważny.
      try {
        const { data } = await api.get("/auth/me", { _skipAuthRedirect: true });
        if (!cancelled) {
          setUser(data);
          setForcePasswordChange(!!data.force_password_change);
          try { localStorage.setItem(USER_KEY, JSON.stringify(data)); }
          catch (err) { console.warn("[auth] bootstrap user cache failed:", err); }
        }
      } catch {
        clearAuth();
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    bootstrap();
    return () => {
      cancelled = true;
    };
  }, []);

  async function login(email, password) {
    const { data } = await api.post("/auth/login", { email, password });
    // Backend ustawił cookies (httpOnly). My zapisujemy tylko user info dla UX.
    setTokens({ user: data.user });
    setUser(data.user);
    setForcePasswordChange(!!data.force_password_change);
    return data;
  }

  async function logout() {
    try {
      // P3-XSS-hardening (2026-07-10) — refresh token pobierany przez backend
      // z httpOnly cookie. Body puste.
      await api.post("/auth/logout", {});
    } catch (err) { console.warn("[auth] logout API call failed:", err); }
    clearAuth();
    setUser(null);
    setForcePasswordChange(false);
  }

  async function changePassword(current_password, new_password) {
    await api.post("/auth/change-password", { current_password, new_password });
    // Force re-login
    clearAuth();
    setUser(null);
    setForcePasswordChange(false);
  }

  /**
   * P2g (2026-07-05) — odświeżenie profilu użytkownika po zmianie języka.
   * Używane przez LanguageProvider gdy user zmienia język — trzeba przeładować
   * `user.language` żeby cała aplikacja natychmiast się przetłumaczyła.
   */
  async function refresh() {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      try { localStorage.setItem(USER_KEY, JSON.stringify(data)); }
      catch (err) { console.warn("[auth] refresh cache failed:", err); }
    } catch { /* silent — użytkownik zapewne wylogowany */ }
  }

  const value = { user, loading, forcePasswordChange, login, logout, changePassword, setUser, refresh };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
}

export function hasRole(user, ...roles) {
  if (!user) return false;
  return roles.includes(user.role);
}
